/**
 * Venue confirmation — the shared record + banner + peer-sync machinery for
 * "Met at {venue}" (tiered-exchange Plan 1, Step 7d; refactor-log row
 * 2026-07-28: three pages carried drifted ~80-line copies — this is the one
 * home going forward; the People inline row is built on it, and the dormant
 * chat-page copies retire when those surfaces are next touched).
 *
 * Host-side only (the venue gate bites Anon surfaces; hosts confirm freely).
 */

import { sendMessage } from '../chat/chatService';
import { persistMessage } from '../chat/messageStore';
import { buildSystemMeetingMessage, buildMetadataUpdatedMessage } from '../chat/messageBuilders';
import { formatSystemMessageLocation } from '../../utils/locationFormatters';
import { getProfileUserId } from '../../utils/identityUtils';
import { setMeetingVenue, updateMeetingVenue } from '../meetings/meetingRows';
import { saveCustomVenue } from '../location/providers/radarProvider';
import { logConnectionLocation } from '../analytics/eventLogger';

/** Flip the confirmation flag — the check-tap shape. Pure. */
export const buildVenueConfirmationMetadata = (metadata) => ({
  ...metadata,
  venueConfirmed: true,
  lastUpdated: new Date().toISOString(),
});

/**
 * Apply a picked venue — the pencil-edit shape (a pick is itself a
 * confirmation). Pure; mirrors the chat page's onVenueChange block.
 */
export const buildVenueChangeMetadata = (metadata, selectedVenue) => {
  const address = metadata?.locationData?.venueInfo?.address || {};
  const updated = {
    ...metadata,
    venueConfirmed: true,
    lastUpdated: new Date().toISOString(),
    locationData: {
      ...metadata?.locationData,
      venueInfo: {
        ...metadata?.locationData?.venueInfo,
        venue: selectedVenue,
      },
    },
  };

  const parts = [];
  if (address.streetNumber && address.street) {
    parts.push(`${address.streetNumber} ${address.street}`);
  } else if (address.street) {
    parts.push(address.street);
  }
  if (selectedVenue?.name) {
    if (address.cityAbbr || address.city) parts.push(address.cityAbbr || address.city);
    updated.location = `${selectedVenue.name}${parts.length > 0 ? ', ' + parts.join(', ') : ''}`;
  } else {
    if (address.neighborhood) parts.push(address.neighborhood);
    if (address.cityAbbr || address.city) parts.push(address.cityAbbr || address.city);
    updated.location = parts.join(', ');
  }
  return updated;
};

/**
 * The shared tail, page-free: re-persist the origin banner (the formatter
 * gate now passes the confirmed venue — the peer's banner upgrades on their
 * side) and push metadata_updated to the peer. Chat-state updates are the
 * caller's concern (none exist on People).
 */
export const syncVenueToPeer = async (chatRoomId, updatedMetadata) => {
  const newContent = formatSystemMessageLocation(updatedMetadata);
  if (newContent) {
    await persistMessage(
      chatRoomId,
      buildSystemMeetingMessage({
        id: `system_${chatRoomId}`,
        content: newContent,
        meetingDate: updatedMetadata.meetingDate,
        meetingUtcOffset: updatedMetadata.meetingUtcOffset,
      })
    );
  }

  logConnectionLocation(chatRoomId, updatedMetadata.locationData, 'manual');

  try {
    await sendMessage(
      chatRoomId,
      buildMetadataUpdatedMessage({
        connectionMetadata: updatedMetadata,
        senderProfileUserId: getProfileUserId(),
      })
    );
  } catch (err) {
    console.error('[VENUE_CONFIRM] metadata_updated send failed:', err);
  }
};

/** Check-tap: flip + persist (context method) + shared tail. */
export const confirmVenue = async (chatRoomId, metadata, { updateConnectionMetadata }) => {
  if (!metadata) return null;
  const updated = buildVenueConfirmationMetadata(metadata);
  await updateConnectionMetadata(chatRoomId, updated);
  // The meeting row was seeded without the venue (gated at the write) — this
  // confirmation is what puts it there (same rail as changeVenue)
  const venueInfo = updated.locationData?.venueInfo;
  if (venueInfo) setMeetingVenue(chatRoomId, getProfileUserId(), updated.meetingKey, venueInfo);
  await syncVenueToPeer(chatRoomId, updated);
  return updated;
};

/**
 * Pencil-pick: custom-venue save + build + persist + meeting row + tail.
 * `peerSync: false` keeps the change on this side only (a PXP venue is
 * per-user; a typed record has no peer) — the analytics call still fires.
 */
export const changeVenue = async (
  chatRoomId,
  metadata,
  selectedVenue,
  { updateConnectionMetadata, peerSync = true }
) => {
  const coords = metadata?.locationData?.coords || {};
  if (selectedVenue?.isCustom && selectedVenue?.name && coords.latitude && coords.longitude) {
    saveCustomVenue(selectedVenue.name, coords.latitude, coords.longitude);
  }
  const updated = buildVenueChangeMetadata(metadata, selectedVenue);
  await updateConnectionMetadata(chatRoomId, updated);
  setMeetingVenue(
    chatRoomId,
    getProfileUserId(),
    updated.meetingKey,
    updated.locationData.venueInfo
  );
  if (peerSync) await syncVenueToPeer(chatRoomId, updated);
  else logConnectionLocation(chatRoomId, updated.locationData, 'manual');
  return updated;
};

/**
 * Correct an earlier meeting's venue (signup D1: every meeting is editable any
 * time, DETAIL §5) — that meeting's own row only; meeting rows are per-side,
 * so nothing syncs to the peer. Returns the venueInfo written.
 */
export const changeMeetingVenue = async (chatRoomId, meetingId, metadata, selectedVenue) => {
  const { venueInfo } = buildVenueChangeMetadata(metadata, selectedVenue).locationData;
  await updateMeetingVenue(meetingId, venueInfo);
  logConnectionLocation(chatRoomId, { venueInfo }, 'manual');
  return venueInfo;
};
